import { DIMENSIONS } from "./constants";

/*
  The cross-filter model. Pure functions, no React, so the semantics can be unit-tested on their own.
  Ported from the reference build's filters.js.

  `filters` is `{ [dimension]: Set<string> }`. OR within a dimension, AND across dimensions. A dimension with no
  values is deleted, never left as an empty Set (an empty Set would filter every lead out).
*/

export const EMPTY_FILTERS = Object.freeze({});

// A lead survives if it matches EVERY active dimension.
export function applyFilters(leads, filters) {
  const active = Object.keys(filters);
  if (!active.length) { return leads; } // same reference, so downstream memoisation is a no-op

  const checks = active.map((dimension) => [dimension, filters[dimension]]);

  return leads.filter((lead) => {
    for (let i = 0; i < checks.length; i++) {
      if (!checks[i][1].has(lead[checks[i][0]])) { return false; }
    }
    return true;
  });
}

export const isActive = (filters) => Object.keys(filters).length > 0;

// A selection `{ dimension, values[] }` is "on" when every one of its values is selected.
export const isSelected = (filters, selection) => {
  const set = selection && filters[selection.dimension];
  return !!set && selection.values.length > 0 && selection.values.every((v) => set.has(v));
};

const withDimension = (filters, dimension, values) => {
  const next = { ...filters };
  if (values.size) { next[dimension] = values; } else { delete next[dimension]; }
  return next;
};

/*
  Every case returns a NEW outer object and a NEW Set for the dimension it touched. Mutating a Set in place
  leaves the object identity unchanged, React sees no change and nothing re-renders.
*/
export function filterReducer(filters, action) {
  switch (action.type) {
    case "toggle": {
      const { dimension, values } = action.selection || {};
      if (!DIMENSIONS.includes(dimension) || !(values || []).length) { return filters; }

      const next = new Set(filters[dimension] || []);
      const allOn = values.every((v) => next.has(v));
      values.forEach((v) => { if (allOn) { next.delete(v); } else { next.add(v); } });

      return withDimension(filters, dimension, next);
    }

    case "remove": {
      const { dimension, value } = action;
      if (!filters[dimension]) { return filters; }

      const next = new Set(filters[dimension]);
      next.delete(value);

      return withDimension(filters, dimension, next);
    }

    case "set":
      return action.filters || EMPTY_FILTERS;

    case "clear":
      return isActive(filters) ? EMPTY_FILTERS : filters;

    default:
      return filters;
  }
}
